import { configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { persistReducer, persistStore } from "redux-persist";
import { authApi } from "./api/authApi";
import authReducer from "./slice/authSlice";
import notificationReducer from "./slice/notificationSlice";
import authForgotPasswordReducer from "./slice/authForgotPasswordSlice";
import modalReducer from "./slice/modalSlice";
import { postApi } from "./api/postApi";
import { financeApi } from "./api/financeApi";
import { notificationApi } from "./api/notificationApi";
import { trendingStocksApi } from "./api/trendingStocksApi";
import { leaderboardApi } from "./api/leaderboardApi";
import { userApi } from "./api/userApi";
import { uploadFileApi } from "./api/uploadFileApi";
import { postCommentApi } from "./api/postCommentApi";
import { reportPostApi } from "./api/reportPostApi";
import { walletApi } from "@/store/api/walletApi";
import { oembedApi } from "./api/oembedApi";
import loadingReducer from "./slice/loadingSlice";
import { premiumApi } from "./api/premiumApi";
import signUpReducer from "./slice/signUpSlice";
import postParamsReducer from "./slice/postParamsSlice";
import commentPendingReducer from "./slice/commentPendingSlice";
import { watchlistFiltersApi } from "./api/watchlistFiltersApi";
import rightSidebarReducer from "./slice/rightSidebarSlice";
import { focusListApi } from "./api/focusListApi";
import { draftsApi } from "./api/draftsApi";
import conversionReducer from "./slice/conversionSlice";
import updatedPostsReducer from "./slice/updatedPostsSlice";
import conversationsUnreadReducer from "./slice/conversationsUnreadSlice";
import { contactUsApi } from "./api/contactUsApi";
import { FaqApi } from "./api/faqApi";
import { conversationsApi } from "./api/conversationsApi";

const persistConfig = {
  key: "auth",
  storage,
  whitelist: ["user"],
};

const persistedAuthReducer = persistReducer(persistConfig, authReducer);

const persistSignUpConfig = {
  key: "signUp",
  storage,
  blacklist: ["avatar"],
};

const persistedSignUpReducer = persistReducer(persistSignUpConfig, signUpReducer);

export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    notificationState: notificationReducer,
    modalState: modalReducer,
    authForgotPassword: authForgotPasswordReducer,
    loading: loadingReducer,
    conversion: conversionReducer,
    signUp: persistedSignUpReducer,
    postParams: postParamsReducer,
    commentPending: commentPendingReducer,
    rightSidebar: rightSidebarReducer,
    updatedPosts: updatedPostsReducer,
    conversationsUnread: conversationsUnreadReducer,
    [authApi.reducerPath]: authApi.reducer,
    [postApi.reducerPath]: postApi.reducer,
    [financeApi.reducerPath]: financeApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [trendingStocksApi.reducerPath]: trendingStocksApi.reducer,
    [leaderboardApi.reducerPath]: leaderboardApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [uploadFileApi.reducerPath]: uploadFileApi.reducer,
    [postCommentApi.reducerPath]: postCommentApi.reducer,
    [reportPostApi.reducerPath]: reportPostApi.reducer,
    [walletApi.reducerPath]: walletApi.reducer,
    [oembedApi.reducerPath]: oembedApi.reducer,
    [premiumApi.reducerPath]: premiumApi.reducer,
    [watchlistFiltersApi.reducerPath]: watchlistFiltersApi.reducer,
    [focusListApi.reducerPath]: focusListApi.reducer,
    [draftsApi.reducerPath]: draftsApi.reducer,
    [contactUsApi.reducerPath]: contactUsApi.reducer,
    [FaqApi.reducerPath]: FaqApi.reducer,
    [conversationsApi.reducerPath]: conversationsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      authApi.middleware,
      postApi.middleware,
      financeApi.middleware,
      notificationApi.middleware,
      trendingStocksApi.middleware,
      leaderboardApi.middleware,
      userApi.middleware,
      uploadFileApi.middleware,
      postCommentApi.middleware,
      reportPostApi.middleware,
      walletApi.middleware,
      oembedApi.middleware,
      premiumApi.middleware,
      watchlistFiltersApi.middleware,
      focusListApi.middleware,
      draftsApi.middleware,
      contactUsApi.middleware,
      FaqApi.middleware,
      conversationsApi.middleware
    ),
});
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
